import { faPen, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useAtom } from 'jotai';
import { usePopupState } from 'material-ui-popup-state/hooks';
import { useRef } from 'react';
import { useNavigate } from 'react-router';

import CheckPermissions from '@/components/CheckPermissions';
import { PERMISSIONS } from '@/constants/auth.constants';
import {
  assetIntegrationsAtom,
  assetIntegrationsDialogModeAtom,
  assetIntegrationsDialogOpenAtom,
  selectedAssetIntegrationAtom,
  useDeleteAssetIntegration
} from '@/features/AdminAssetDataProviderIntegration/AssetDataProviderIntegration.store';
import type { AssetIntegrationDTO } from '@/features/AdminAssetDataProviderIntegration/AssetDataProviderIntegration.types';
import useConfirm from '@/hooks/useConfirm';
import usePermissionsCheck from '@/hooks/usePermissionsCheck.tsx';
import {
  ASSET_DATA_PROVIDER_LABELS,
  ASSET_DATA_PROVIDERS
} from '@/pages/Assets/Assets.constants';

import useStyles from './AssetDataProviderTable.styles';

const actionsFormatter = ({
  row,
  handleDelete,
  handleUpdate
}: {
  row: AssetIntegrationDTO;
  handleDelete: (id: number) => void;
  handleUpdate: (row: AssetIntegrationDTO) => void;
}) => {
  const isGoogleWorkspaceRow =
    row.provider === ASSET_DATA_PROVIDERS.GOOGLE_WORKSPACE;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      {
        <CheckPermissions permissions={PERMISSIONS.ASSET_UPDATE}>
          <Button onClick={() => handleUpdate(row)}>
            <FontAwesomeIcon icon={faPen} size="lg" />
          </Button>
        </CheckPermissions>
      }
      {!isGoogleWorkspaceRow && (
        <CheckPermissions permissions={PERMISSIONS.ASSET_DELETE}>
          <Button onClick={() => handleDelete(row.id)}>
            <FontAwesomeIcon icon={faTrash} size="lg" />
          </Button>
        </CheckPermissions>
      )}
    </Box>
  );
};

const managedFormatter = ({ managed }: { managed: boolean }) =>
  managed ? <Chip label="Yes" color="success" /> : <Chip label="No" />;

const disabledFormatter = ({
  disabled,
  provider
}: {
  disabled: boolean | null;
  provider: string;
}) => {
  if (provider === ASSET_DATA_PROVIDERS.GOOGLE_WORKSPACE) {
    return <Chip label="N/A" color="default" />;
  }
  return disabled ? (
    <Chip label="Yes" color="warning" />
  ) : (
    <Chip label="No" color="default" />
  );
};

const AssetDataProviderTable = () => {
  const [assetIntegrations] = useAtom(assetIntegrationsAtom);
  const [, setAssetIntegrationsDialogMode] = useAtom(
    assetIntegrationsDialogModeAtom
  );
  const [, setAssetIntegrationsDialogOpen] = useAtom(
    assetIntegrationsDialogOpenAtom
  );
  const [selectedAssetIntegration, setSelectedAssetIntegration] = useAtom(
    selectedAssetIntegrationAtom
  );
  const navigate = useNavigate();

  const ref = useRef<HTMLTableSectionElement>(null);
  const { classes } = useStyles();

  const deleteAssetIntegration = useDeleteAssetIntegration();
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'moreMenu'
  });

  const {
    ConfirmationModal: AssetIntegrationDeleteModal,
    confirm: confirmDelete
  } = useConfirm({
    title: `Delete Asset Integration: (#${selectedAssetIntegration?.id})`,
    message: 'Please confirm that you want to delete this asset provider.',
    button: 'Delete'
  });

  const handleDelete = async (id: number) => {
    const selectedIntegration = assetIntegrations.find(
      (integration) => integration.id === id
    );
    setSelectedAssetIntegration(selectedIntegration);

    const confirmed = await confirmDelete();
    if (confirmed && id !== undefined) {
      deleteAssetIntegration.mutate(id);
    }
    popupState.close();
  };

  const handleUpdate = (row: AssetIntegrationDTO) => {
    if (row.provider === ASSET_DATA_PROVIDERS.GOOGLE_WORKSPACE) {
      if (!row.source_type_id || !row.device_uid || !row.source_uid) {
        return;
      }

      const searchParams = new URLSearchParams({
        q: JSON.stringify({ source_type_id: row.source_type_id.toString() }),
        pg: 'CS',
        duid: row.device_uid,
        suid: row.source_uid
      });
      navigate(`/devices/cloud-sources?${searchParams.toString()}`);
      return;
    }

    const selectedIntegration = assetIntegrations.find(
      (integration) => integration.id === row.id
    );

    setSelectedAssetIntegration(selectedIntegration);
    setAssetIntegrationsDialogMode('edit');
    setAssetIntegrationsDialogOpen(true);
  };

  const COLUMNS = [
    {
      id: 'id',
      label: 'ID',
      cell: (row: AssetIntegrationDTO) => row.id,
      width: 25
    },
    {
      id: 'provider',
      label: 'Provider',
      cell: (row: AssetIntegrationDTO) =>
        ASSET_DATA_PROVIDER_LABELS[row.provider] || row.provider.toUpperCase(),
      width: 220
    },
    {
      id: 'managed',
      label: 'Managed',
      cell: (row: AssetIntegrationDTO) =>
        managedFormatter({ managed: row.managed }),
      width: 150
    },
    {
      id: 'tenant_id',
      label: 'Tenant ID',
      cell: (row: AssetIntegrationDTO) => row.tenant_id,
      width: 150
    },
    {
      id: 'app_id',
      label: 'App ID',
      cell: (row: AssetIntegrationDTO) =>
        row.provider === 'msdefender' ? row.base_url : '—',
      width: 200
    },
    {
      id: 'base_url',
      label: 'Base URL',
      cell: (row: AssetIntegrationDTO) =>
        row.provider === 'msdefender' ? '—' : row.base_url,
      width: 200
    },
    {
      id: 'api_token',
      label: 'API Token',
      cell: (row: AssetIntegrationDTO) => {
        const token = row.api_token;
        return token && token.length > 4 ? `******${token.slice(-4)}` : token;
      },
      width: 200
    },
    {
      id: 'disabled',
      label: 'Disabled',
      cell: (row: AssetIntegrationDTO) =>
        disabledFormatter({ disabled: row.disabled, provider: row.provider }),
      width: 200
    },
    {
      id: 'asset_count',
      label: 'Asset Count',
      width: 100
    },
    {
      id: 'actions',
      label: 'Actions',
      cell: (row: AssetIntegrationDTO) =>
        actionsFormatter({
          row,
          handleDelete,
          handleUpdate
        }),
      hidden: !usePermissionsCheck(['internal', 'assets.create']),
      width: 100
    }
  ];

  return (
    <>
      <TableContainer
        ref={ref}
        className={classes.assetDataProviderTableContainer}
      >
        {assetIntegrations.length === 0 && (
          <Box className={classes.assetDataProviderNoData}>No data</Box>
        )}
        <Table stickyHeader className={classes.assetDataProviderTable}>
          <TableHead>
            <TableRow>
              {COLUMNS.map(
                (column, index) =>
                  !column.hidden && (
                    <TableCell
                      align={
                        column.id === 'actions' || column.id === 'managed'
                          ? 'center'
                          : 'left'
                      }
                      style={{
                        width: column.width,
                        paddingLeft: index === 0 ? 16 : ''
                      }}
                      className={classes.assetDataProviderTableHeaderCell}
                      key={column.id}
                    >
                      {column.label}
                    </TableCell>
                  )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {assetIntegrations.map((row) => (
              <TableRow key={row.id}>
                {COLUMNS.map(
                  (column, index) =>
                    !column.hidden && (
                      <TableCell
                        className={classes.assetDataProviderTableBodyCell}
                        align={
                          column.id === 'actions' || column.id === 'managed'
                            ? 'center'
                            : 'left'
                        }
                        key={column.id}
                        style={{
                          width: column.width,
                          paddingLeft: index === 0 ? 16 : ''
                        }}
                      >
                        {column.cell
                          ? column.cell(row)
                          : row[column.id as keyof AssetIntegrationDTO]}
                      </TableCell>
                    )
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <AssetIntegrationDeleteModal />
    </>
  );
};

export default AssetDataProviderTable;
